'use client'

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { prefersReducedMotion } from './FlyingChip'
import {
  PRICE_ROLL_STEP_MS,
  priceLayout,
  reelText,
  restLine,
  type PricePart
} from './priceRoll'

/** The reel's text is the same twenty lines on every tile — built once. */
const REEL_TEXT = reelText()

/**
 * B40 — the price, as an odometer.
 *
 * Renders `text` as a row of per-digit reels and the fixed runs between them.
 * Every reel RESTS on its own digit, so the figure is correct on the frame it
 * mounts and stays correct with the animation switched off; the roll is one
 * revolution landing on that resting position and nothing else.
 *
 * THE THREE THINGS THAT MAKE IT SAFE, all of them invisible in a diff:
 *
 *  1. EVERY PART PAINTS ITS OWN METAL. The price's gradient may NOT live on an
 *     ancestor. `background-clip: text` on a parent clips that parent's
 *     background to the text of its whole subtree — at the subtree's LAYOUT
 *     geometry, ignoring a descendant's transform — so a gold gradient on
 *     `__pays-value` painted a ghost of each reel's first line ("0") underneath
 *     the digit the reel had rolled to, and every price on the board read as a
 *     smear of two glyphs. The fill is therefore declared on the reel and on the
 *     fixed runs, never above them.
 *  2. ONE CLIPPED WINDOW PER DIGIT, exactly one line tall, with the strip
 *     translated inside it. Both halves are load-bearing: without the window the
 *     whole strip paints, without the transform every cell shows a zero.
 *  3. REDUCED MOTION GETS NO REEL AT ALL — just the number, as plain text.
 *
 * The whole row is `aria-hidden` on the caller's element: the price is already
 * in the tile's accessible name (`view.ariaLabel` — "…pays 6.46x"), and a reel
 * left visible to a screen reader would be read as "zero one two three…".
 *
 * WHY THE GENERATION KEY. The tiles keep their DOM nodes across rounds — they
 * are keyed by option id, and the option ids are the market's, not the round's —
 * so a new price arrives as a prop change on a mounted element and a CSS
 * one-shot would never replay. Bumping a key on the reels remounts them, which
 * is the only way to restart an animation without reading layout. It is a ref
 * rather than state so a price change costs no extra render: the value is only
 * ever spent as a key, so a StrictMode double-invoke bumping it twice is
 * indistinguishable from bumping it once.
 */
export function PriceReel({ text }: Readonly<{ text: string }>) {
  const generation = useRef({ text, count: 0 })
  if (generation.current.text !== text) {
    generation.current = { text, count: generation.current.count + 1 }
  }
  const run = generation.current.count

  // `false` on the server and on the first client render, so the markup the
  // browser hydrates is the markup the server sent; the swap happens on the
  // effect. The reel's resting state is already the finished number, so the one
  // frame in between shows the same figure either way.
  const [still, setStill] = useState(false)
  useEffect(() => {
    setStill(prefersReducedMotion())
  }, [])

  const layout = priceLayout(text, still)
  if (layout.kind === 'plain') {
    return (
      <span className="birdie-tile__roll">
        <span className="birdie-tile__roll-plain">{layout.text}</span>
      </span>
    )
  }

  const parts: readonly PricePart[] = layout.parts
  const cells: JSX.Element[] = []
  let digitsSeen = 0

  for (let index = 0; index < parts.length; index++) {
    const part: PricePart | undefined = parts[index]
    if (part === undefined) continue
    if (part.kind === 'text') {
      cells.push(
        <span className="birdie-tile__roll-fixed" key={`${run}-f${index}`}>
          {part.text}
        </span>
      )
      continue
    }
    // Left to right: the leading digit lands first and the figure resolves the
    // way it is read.
    const delayMs = digitsSeen * PRICE_ROLL_STEP_MS
    digitsSeen++
    cells.push(
      <span className="birdie-tile__roll-cell" key={`${run}-d${index}`}>
        <span
          className="birdie-tile__roll-reel"
          style={
            {
              '--birdie-roll-to': String(restLine(part.digit)),
              '--birdie-roll-delay': `${delayMs}ms`
            } as CSSProperties
          }
        >
          {REEL_TEXT}
        </span>
      </span>
    )
  }

  return <span className="birdie-tile__roll">{cells}</span>
}
